// The city switcher's search, filters and sort (Ben, Oct 12 2026): find a city by its name, coordinates, region or tags;
// filter the list by region and by tags (several at once, a city must wear them all — Oct 13 2026); sort it by name, coordinates, region, tag
// or size (points: Size (High) biggest first, Size (Low) smallest first — Oct 14/15 2026). Pure functions over the game's city
// list (target_char.villages, which front-server already sorts by name) and the player's tag book (game/tagbook.ts).
import type { PlayerVillage } from "@/api/types";
import { tagsOf, type CityTag, type TagBook } from "./tagbook";

export type CitySort = "name" | "coords" | "region" | "tag" | "points" | "pointsLow";
export const CITY_SORTS: { value: CitySort; label: string }[] = [
  { value: "name", label: "Name" },
  { value: "coords", label: "Coords" },
  { value: "region", label: "Region" },
  { value: "tag", label: "Tag" },
  // "points" keeps its Oct 14 value so a device that saved the old "Size (pts)" choice opens on Size (High)
  { value: "points", label: "Size (High)" },
  { value: "pointsLow", label: "Size (Low)" },
];
export const isCitySort = (s: unknown): s is CitySort => CITY_SORTS.some((o) => o.value === s);

/** The tag filter's value for "cities with no tag" (it stands alone: picking it lets go of every tag, and the reverse). */
export const NO_TAG = "-";

export interface CityListView {
  query: string;
  /** a continent number, or null for every region */
  region: number | null;
  /** the tag ids a city must ALL wear (Ben, Oct 13 2026: several tags at once, matching all), [NO_TAG] for the cities with
   *  no tag, or [] for every city */
  tags: string[];
  sort: CitySort;
}

/** "x,y" → [x, y]. */
export const cityXY = (c: { coords: string }): [number, number] => {
  const [x, y] = String(c.coords).split(",").map((n) => Number(n));
  return [Number.isFinite(x) ? x : 0, Number.isFinite(y) ? y : 0];
};

/** The region as the game prints it: R563. */
export const regionLabel = (continent: number) => `R${continent}`;

/** Text as compared: lower case, accents off, runs of whitespace as one space. */
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/** A query that is a pair of coordinates — 505,518 · 505 518 · 505|518 · (505, 518) · 505:518 — or the start of one while
 *  it is typed (505| or 505,5): each number matches the start of the city's x and y. */
const COORDS = /^\(?\s*(\d{1,4})\s*(?:[,|:;/]\s*|\s+)(\d{0,4})\s*\)?$/;

/** Does the city answer the search? A pair of coordinates matches x and y; otherwise every word of the query must appear
 *  in the city's name, its coordinates (505,518 or 505|518), its region (r563) or one of its tags. */
export function cityMatches(c: PlayerVillage, tags: CityTag[], query: string): boolean {
  const q = fold(query);
  if (!q) return true;
  const [x, y] = cityXY(c);
  const pair = q.match(COORDS);
  if (pair) return String(x).startsWith(pair[1]) && String(y).startsWith(pair[2]);
  const hay = [fold(c.city_name), `${x},${y}`, `${x}|${y}`, regionLabel(c.continent).toLowerCase(), ...tags.map((t) => fold(t.name))];
  return q.split(" ").every((word) => hay.some((h) => h.includes(word)));
}

const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });
const byName = (a: PlayerVillage, b: PlayerVillage) => collator.compare(a.city_name, b.city_name) || a.id - b.id;

/** Sorted copy: by name (natural: 2 before 10), by coordinates (x, then y), by region, by the alphabetically first tag
 *  a city wears (cities with no tag last), or by size — points, biggest city first (Size (High)) or smallest first (Size (Low)) (Ben, Oct 14/15 2026). Ties go by name. */
export function sortCities(cities: PlayerVillage[], book: TagBook, sort: CitySort): PlayerVillage[] {
  const list = [...cities];
  if (sort === "coords")
    return list.sort((a, b) => {
      const [ax, ay] = cityXY(a);
      const [bx, by] = cityXY(b);
      return ax - bx || ay - by || byName(a, b);
    });
  if (sort === "region") return list.sort((a, b) => a.continent - b.continent || byName(a, b));
  if (sort === "points") return list.sort((a, b) => (Number(b.points) || 0) - (Number(a.points) || 0) || byName(a, b));
  if (sort === "pointsLow") return list.sort((a, b) => (Number(a.points) || 0) - (Number(b.points) || 0) || byName(a, b));
  if (sort === "tag") {
    const first = new Map(list.map((c) => [c.id, tagsOf(book, c.id).map((t) => t.name).sort(collator.compare)[0] ?? null]));
    return list.sort((a, b) => {
      const ta = first.get(a.id) ?? null;
      const tb = first.get(b.id) ?? null;
      if (ta === null || tb === null) return ta === tb ? byName(a, b) : ta === null ? 1 : -1;
      return collator.compare(ta, tb) || byName(a, b);
    });
  }
  return list.sort(byName);
}

/** Tap a chip in the tag filter: a tag toggles in or out (and lets go of "No tag"); "No tag" toggles alone. */
export function toggleTag(picked: string[], id: string): string[] {
  if (picked.includes(id)) return picked.filter((t) => t !== id);
  return id === NO_TAG ? [NO_TAG] : [...picked.filter((t) => t !== NO_TAG), id];
}

/** The cities the switcher shows: filtered by region, tags (all of those picked) and search, then sorted. */
export function cityList(cities: PlayerVillage[], book: TagBook, view: CityListView): PlayerVillage[] {
  const shown = cities.filter((c) => {
    if (view.region !== null && c.continent !== view.region) return false;
    const tags = tagsOf(book, c.id);
    if (view.tags.includes(NO_TAG) ? tags.length > 0 : !view.tags.every((id) => tags.some((t) => t.id === id))) return false;
    return cityMatches(c, tags, view.query);
  });
  return sortCities(shown, book, view.sort);
}

/** The regions the cities are in, with how many cities each, in order. */
export function regionCounts(cities: PlayerVillage[]): { continent: number; count: number }[] {
  const n = new Map<number, number>();
  for (const c of cities) n.set(c.continent, (n.get(c.continent) ?? 0) + 1);
  return [...n.entries()].sort((a, b) => a[0] - b[0]).map(([continent, count]) => ({ continent, count }));
}

/** The tags the cities wear (in the tag book's order) with how many cities each, and how many cities wear none. */
export function tagCounts(cities: PlayerVillage[], book: TagBook): { tags: { tag: CityTag; count: number }[]; untagged: number } {
  const n = new Map<string, number>();
  let untagged = 0;
  for (const c of cities) {
    const tags = tagsOf(book, c.id);
    if (!tags.length) untagged++;
    for (const t of tags) n.set(t.id, (n.get(t.id) ?? 0) + 1);
  }
  return { tags: book.tags.filter((t) => n.has(t.id)).map((tag) => ({ tag, count: n.get(tag.id)! })), untagged };
}
